<script setup lang="ts">
import { EnrichmentProvider } from '@factoryfixinc/ats-interfaces';
import { TransitionPresets, usePreferredReducedMotion, useTransition } from '@vueuse/core';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import ClearoutLogo from '@/assets/png/enrichment/vendor-clearout.png';
import PdlLogo from '@/assets/png/enrichment/vendor-pdl.png';
import PiplLogo from '@/assets/png/enrichment/vendor-pipl.png';
import TrestleLogo from '@/assets/png/enrichment/vendor-trestle.png';
import FiberLogo from '@/assets/svg/enrichment/vendor-fiber.svg';
import NexxtLogo from '@/assets/svg/enrichment/vendor-nexxt.svg';
import ResumeLibraryLogo from '@/assets/svg/enrichment/vendor-resume-library.svg';
import TwilioLogo from '@/assets/svg/enrichment/vendor-twilio.svg';
import type {
  TracePanelSource,
  TracePanelSourceSummary,
} from '@/components/Common/trace-panel.mapper';

const props = defineProps<{
  startAt?: number;
  summary: TracePanelSourceSummary;
}>();

function sourceTooltipText(source: TracePanelSource): string {
  if (source.errored) return `${source.label} · Errored`;
  return source.attempted ? source.label : `${source.label} · Not attempted`;
}

// aria-label already names the source; drop describedby so it isn't announced twice.
function withoutDescribedBy(activatorProps: Record<string, unknown>): Record<string, unknown> {
  const rest = { ...activatorProps };
  delete rest['aria-describedby'];
  return rest;
}

const SOURCE_LOGOS: Record<EnrichmentProvider, string> = {
  [EnrichmentProvider.PDL]: PdlLogo,
  [EnrichmentProvider.PIPL]: PiplLogo,
  [EnrichmentProvider.TRESTLE]: TrestleLogo,
  [EnrichmentProvider.CLEAROUT]: ClearoutLogo,
  [EnrichmentProvider.NEXXT]: NexxtLogo,
  [EnrichmentProvider.FIBER]: FiberLogo,
  [EnrichmentProvider.RESUME_LIBRARY]: ResumeLibraryLogo,
  // Not rendered by the panel today; verdicts stay Trestle-attributed. Kept for completeness.
  [EnrichmentProvider.TWILIO]: TwilioLogo,
};

const reducedMotion = usePreferredReducedMotion();
const countTarget = ref(0);
const animatedCount = useTransition(countTarget, {
  duration: 1000,
  transition: TransitionPresets.easeOutCubic,
  disabled: computed(() => reducedMotion.value === 'reduce'),
});
const displayedCount = computed(() => Math.round(animatedCount.value));
const startDelayMs = computed(() => Math.max(0, (props.startAt ?? Date.now()) - Date.now()));
let countTimeoutId: number | null = null;

function scheduleCountUp(): void {
  if (countTimeoutId !== null) window.clearTimeout(countTimeoutId);
  countTarget.value = 0;
  countTimeoutId = window.setTimeout(() => {
    countTarget.value = props.summary.count;
    countTimeoutId = null;
  }, startDelayMs.value);
}

watch(() => props.startAt, scheduleCountUp);
onMounted(scheduleCountUp);
onBeforeUnmount(() => {
  if (countTimeoutId !== null) window.clearTimeout(countTimeoutId);
});
</script>

<template>
  <section
    data-test-id="enrichment-trace-source-summary"
    class="source-summary mb-[13px] flex flex-col items-center gap-[7px] rounded-[10px] border border-enrichment-band-edge bg-enrichment-band px-2.5 pb-[9px] pt-2.5"
    :style="{ '--enrichment-celebrate-delay': `${startDelayMs}ms` }"
  >
    <span class="flex items-center gap-2">
      <span class="sr-only">{{ summary.count }}</span>
      <span
        aria-hidden="true"
        class="min-w-4 text-[26px] font-extrabold leading-none text-primary-500 [font-variant-numeric:tabular-nums]"
        data-test-id="enrichment-trace-source-count"
      >
        {{ displayedCount }}
      </span>
      <span class="text-[13.5px] font-650 tracking-[0.2px] text-shade-800">
        {{ summary.sources.length ? 'sources checked' : 'independent sources checked' }}
      </span>
    </span>

    <template v-if="summary.sources.length">
      <span class="mx-0.5 h-px self-stretch bg-enrichment-band-edge" aria-hidden="true" />

      <span class="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-1.5">
        <v-tooltip
          v-for="source in summary.sources"
          :key="source.provider"
          location="top"
          content-class="insights-chip-tooltip"
        >
          <template #activator="{ props: activatorProps }">
            <span
              v-bind="withoutDescribedBy(activatorProps)"
              tabindex="0"
              role="img"
              :aria-label="sourceTooltipText(source)"
              class="inline-flex shrink-0 items-center justify-center rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
              :data-test-id="`enrichment-trace-source-${source.provider}`"
            >
              <img
                :src="SOURCE_LOGOS[source.provider]"
                alt=""
                aria-hidden="true"
                class="h-6 w-6 rounded-full"
                :class="source.attempted ? '' : 'opacity-50 grayscale'"
              />
            </span>
          </template>
          <span>{{ sourceTooltipText(source) }}</span>
        </v-tooltip>
      </span>
    </template>
  </section>
</template>

<style scoped lang="postcss">
.source-summary {
  animation: trace-stat-in 300ms var(--enrichment-celebrate-delay) cubic-bezier(0.2, 0.8, 0.2, 1)
    both;
}

@keyframes trace-stat-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes trace-source-fade {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .source-summary {
    animation: trace-source-fade 120ms ease-out both;
  }
}
</style>
